// Overlay rendering: ID badge, editing status, visibility/hidden masks, connection status

import { drawText as canvasDrawText } from '@/services/canvas/text/CanvasTextService';
import useColorUtils from '@/utils/colorUtils';
import useShapeUtils from '@/modules/Node/utils/shapeUtils';
import { getLinePatternCanvas, getSquaredPatternCanvas } from '@/modules/Node/utils/patternUtils';
import type { EditingStatus } from '@/modules/Node';

const { hexToRGB } = useColorUtils();
const { addShapeToContext } = useShapeUtils();
const DEFAULT_ID_COLOR_RGB = hexToRGB('#2750e2');

interface OverlayState {
  id: number | undefined;
  shape: string;
  width: number;
  height: number;
  border: boolean;
  borderWidth: number;
  xCache: number | null;
  yCache: number | null;
  globalAlpha: number;
  connectSign: boolean;
  hiddenLocal: boolean;
  hidden: boolean;
  locked: boolean;
  visibilityMin: number;
  visibilityMax: number;
  currentLayerId: number;
  editingContent: EditingStatus | false;
  editingAppearance: EditingStatus | false;
}

export function drawId(ctx: CanvasRenderingContext2D, state: OverlayState): void {
  const ICON_FONT_SIZE = 10;
  const LOCK_ICON = '\uf023';
  const HIDDEN_ICON = '\uf070';
  const defaultIdWidth = 20;
  const defaultIdHeight = 12;
  const connectSign = state.connectSign ? ' +' : '';
  const computedBorder = state.border ? state.borderWidth : 0;
  const idBounds = {
    x: state.xCache! + state.width / 4,
    y: state.yCache! - (state.height / 2 + computedBorder + defaultIdHeight),
    width: defaultIdWidth,
    height: defaultIdHeight,
  };
  const idFontConfig = { fontSize: 10, fontFamily: 'Arial', fontWeight: '', fontStyle: '', lineHeight: 10 };

  ctx.beginPath();
  ctx.save();

  ctx.fillStyle = `rgba(${DEFAULT_ID_COLOR_RGB.r}, ${DEFAULT_ID_COLOR_RGB.g}, ${DEFAULT_ID_COLOR_RGB.b}, ${state.globalAlpha})`;

  if (!state.hiddenLocal) {
    canvasDrawText(ctx, `${String(state.id)}${connectSign}`, idBounds, idFontConfig, { align: 'left' });
    ctx.beginPath();
    if (state.locked || state.hidden) {
      ctx.font = `900 ${ICON_FONT_SIZE}px "Font Awesome 5 Free"`;
      const lockIcon = state.locked ? LOCK_ICON : '';
      const hiddenIcon = state.hidden ? HIDDEN_ICON : '';
      const LOCK_ICON_WIDTH = 12;
      const HIDDEN_ICON_WIDTH = 15;
      const ICONS_DIFF = 3;
      const iconComputedWidth = state.hidden && state.locked
        ? state.width / 4 - LOCK_ICON_WIDTH - HIDDEN_ICON_WIDTH
        : state.hidden
          ? state.width / 4 - ICONS_DIFF - HIDDEN_ICON_WIDTH
          : state.width / 4 - LOCK_ICON_WIDTH;

      ctx.fillText(
        `${lockIcon} ${hiddenIcon}`,
        state.xCache! + iconComputedWidth,
        state.yCache! + ICON_FONT_SIZE - (state.height / 2 + computedBorder + defaultIdHeight),
      );
    }
  }
  ctx.restore();
  ctx.closePath();
}

export function drawEditing(ctx: CanvasRenderingContext2D, state: OverlayState): void {
  ctx.beginPath();
  ctx.save();

  const MIN_NODE_WIDTH = 60;
  const STATUS_LAYER_HEIGHT = 29;
  const STATUS_LAYER_PADDING = 2;
  const STATUS_LAYER_COLOR = 'rgba(60, 60, 60, .54)';
  const STATUS_FONT_SIZE = 15;
  const STATUS_FONT_COLOR_DEFAULT = 'rgba(247, 251, 251, 0.42)';
  const STATUS_APPEARANCE_ICON = '\uf1fc';
  const STATUS_CONTENT_ICON = '\uf03a';

  const borderWidth = state.border ? state.borderWidth : 0;
  ctx.font = `900 ${STATUS_FONT_SIZE}px "Font Awesome 5 Free"`;

  if (state.width >= MIN_NODE_WIDTH) {
    ctx.beginPath();
    ctx.fillStyle = STATUS_LAYER_COLOR;
    ctx.rect(
      state.xCache! - state.width / 2 - borderWidth,
      state.yCache! - state.height / 2 - borderWidth,
      state.width + borderWidth * 2,
      state.height + borderWidth * 2,
    );
    ctx.rect(
      state.xCache! - state.width / 2 - borderWidth,
      state.yCache! + state.height / 2 + borderWidth + STATUS_LAYER_PADDING,
      state.width + borderWidth * 2,
      STATUS_LAYER_HEIGHT,
    );
    if (!state.hiddenLocal) ctx.fill();

    ctx.beginPath();
    ctx.fillStyle = state.editingAppearance ? state.editingAppearance.color : STATUS_FONT_COLOR_DEFAULT;
    ctx.fillText(
      STATUS_APPEARANCE_ICON,
      state.xCache! - state.width / 4 - borderWidth - STATUS_FONT_SIZE / 2,
      state.yCache! + state.height / 2 + borderWidth + STATUS_LAYER_HEIGHT / 2 + STATUS_FONT_SIZE / 2,
    );

    ctx.fillStyle = state.editingContent ? state.editingContent.color : STATUS_FONT_COLOR_DEFAULT;
    ctx.fillText(
      STATUS_CONTENT_ICON,
      state.xCache! + state.width / 4 + borderWidth - STATUS_FONT_SIZE / 2,
      state.yCache! + state.height / 2 + borderWidth + STATUS_LAYER_HEIGHT / 2 + STATUS_FONT_SIZE / 2,
    );
    if (!state.hiddenLocal) ctx.fill();
  } else if (state.height >= STATUS_LAYER_HEIGHT) {
    ctx.beginPath();
    ctx.fillStyle = STATUS_LAYER_COLOR;
    ctx.rect(
      state.xCache! - state.width / 2 - borderWidth,
      state.yCache! - state.height / 2 - borderWidth,
      state.width + borderWidth * 2,
      state.height + borderWidth * 2,
    );
    ctx.rect(
      state.xCache! + state.width / 2 + borderWidth + STATUS_LAYER_PADDING,
      state.yCache! - state.height / 2 - borderWidth,
      MIN_NODE_WIDTH,
      state.height + borderWidth * 2,
    );
    if (!state.hiddenLocal) ctx.fill();

    ctx.beginPath();
    ctx.fillStyle = state.editingAppearance ? state.editingAppearance.color : STATUS_FONT_COLOR_DEFAULT;
    ctx.fillText(
      STATUS_APPEARANCE_ICON,
      state.xCache! + state.width / 2 + borderWidth + MIN_NODE_WIDTH / 2 - STATUS_FONT_SIZE * 1.2,
      state.yCache! + STATUS_FONT_SIZE / 2,
    );

    ctx.fillStyle = state.editingContent ? state.editingContent.color : STATUS_FONT_COLOR_DEFAULT;
    ctx.fillText(
      STATUS_CONTENT_ICON,
      state.xCache! + state.width / 2 + borderWidth + MIN_NODE_WIDTH / 2 + (STATUS_FONT_SIZE * 1.2) / 2,
      state.yCache! + STATUS_FONT_SIZE / 2,
    );
    if (!state.hiddenLocal) ctx.fill();
  } else {
    ctx.beginPath();
    ctx.fillStyle = STATUS_LAYER_COLOR;
    ctx.rect(
      state.xCache! - STATUS_LAYER_HEIGHT / 2,
      state.yCache! - STATUS_LAYER_HEIGHT / 2,
      STATUS_LAYER_HEIGHT,
      STATUS_LAYER_HEIGHT,
    );
    ctx.rect(
      state.xCache! + STATUS_LAYER_HEIGHT / 2 + STATUS_LAYER_PADDING,
      state.yCache! - STATUS_LAYER_HEIGHT / 2,
      MIN_NODE_WIDTH,
      STATUS_LAYER_HEIGHT,
    );
    if (!state.hiddenLocal) ctx.fill();

    ctx.beginPath();
    ctx.fillStyle = state.editingAppearance ? state.editingAppearance.color : STATUS_FONT_COLOR_DEFAULT;
    ctx.fillText(
      STATUS_APPEARANCE_ICON,
      state.xCache! + STATUS_LAYER_HEIGHT / 2 + MIN_NODE_WIDTH / 2 - STATUS_FONT_SIZE * 1.2,
      state.yCache! - STATUS_LAYER_HEIGHT / 2 + (STATUS_LAYER_HEIGHT - STATUS_FONT_SIZE) / 2 + STATUS_FONT_SIZE * 0.9,
    );

    ctx.fillStyle = state.editingContent ? state.editingContent.color : STATUS_FONT_COLOR_DEFAULT;
    ctx.fillText(
      STATUS_CONTENT_ICON,
      state.xCache! + STATUS_LAYER_HEIGHT / 2 + MIN_NODE_WIDTH / 2 + (STATUS_FONT_SIZE * 1.2) / 2,
      state.yCache! - STATUS_LAYER_HEIGHT / 2 + (STATUS_LAYER_HEIGHT - STATUS_FONT_SIZE) / 2 + STATUS_FONT_SIZE * 0.9,
    );
    if (!state.hiddenLocal) ctx.fill();
  }

  ctx.restore();
  ctx.closePath();
}

export function drawVisibilityMask(ctx: CanvasRenderingContext2D, state: OverlayState): void {
  if (state.visibilityMin > state.currentLayerId || state.visibilityMax < state.currentLayerId) {
    ctx.save();
    addShapeToContext({
      ctx,
      node: state,
      center: { x: state.xCache!, y: state.yCache! },
    });
    ctx.clip();
    fillPattern(ctx, state, getLinePatternCanvas());
    ctx.restore();
  }
}

export function drawHiddenMask(ctx: CanvasRenderingContext2D, state: OverlayState): void {
  ctx.save();
  addShapeToContext({
    ctx,
    node: state,
    center: { x: state.xCache!, y: state.yCache! },
  });
  ctx.clip();
  fillPattern(ctx, state, getSquaredPatternCanvas());
  ctx.restore();
}

function fillPattern(ctx: CanvasRenderingContext2D, state: OverlayState, patternCanvas: HTMLCanvasElement): void {
  const radiusX = state.border ? state.width / 2 + state.borderWidth : state.width / 2;
  const radiusY = state.border ? state.height / 2 + state.borderWidth : state.height / 2;
  ctx.beginPath();
  const initialX = state.xCache! - radiusX;
  const initialY = state.yCache! - radiusY;
  ctx.fillStyle = ctx.createPattern(patternCanvas, 'repeat')!;
  ctx.fillRect(initialX, initialY, radiusX * 2, radiusY * 2);
  ctx.closePath();
}

export function drawConnectionReadyStatus(ctx: CanvasRenderingContext2D, state: OverlayState): void {
  ctx.beginPath();
  ctx.save();
  const radius = state.border ? state.width / 2 + state.borderWidth : state.width / 2;
  addShapeToContext({
    ctx,
    node: state,
    center: { x: state.xCache!, y: state.yCache! },
  });
  ctx.clip();

  ctx.translate(state.xCache!, state.yCache!);
  ctx.rotate(45);
  ctx.translate(-state.xCache!, -state.yCache!);

  ctx.moveTo(state.xCache!, state.yCache! - radius);
  ctx.lineTo(state.xCache!, state.yCache! + radius);

  ctx.fillStyle = '#ffffff';
  ctx.fill();

  ctx.strokeStyle = '#BFBFBF';
  ctx.lineWidth = Math.floor(state.width / 6);
  ctx.stroke();

  ctx.restore();
  ctx.closePath();
}

export type { OverlayState };
